import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "../../../common/u1/cn";
import { CompanyMark } from "../../../common/u1/CompanyMark";
import { ChevronDown, CheckIcon } from "../../../../assets/ui/icons";
import {
  enterpriseLabel,
  enterpriseSubtitle,
  enterpriseTone,
} from "./enterpriseIdentity";

/* "Tracking as" — which programme's buyers this screen is showing. */

const DOT_TONES = {
  unlimited: "bg-[var(--navy)]",
  funded: "bg-[var(--success)]",
  empty: "bg-[var(--muted-2)]",
};

/* The mark, the name and the line beneath it — the same block in the trigger and in every row, so the chosen programme looks identical in both. */
const Identity = ({ enterprise, oneLine = false }) => (
  <>
    <span className="shrink-0">
      <CompanyMark
        company={{
          name: enterpriseLabel(enterprise),
          registered_company_name: enterprise?.registered_company_name,
          companyID: enterprise?.company,
        }}
        size="xs"
        tone="brand"
        /* The star marks a programme with credit to spend, which is what a
           member badge means here. */
        member={enterpriseTone(enterprise) !== "empty"}
      />
    </span>

    <span className="min-w-0 flex-1">
      <span
        className={cn(
          "block text-[13.5px] font-bold text-[var(--ink)] font-sans",
          oneLine ? "truncate" : "break-words"
        )}
      >
        {enterpriseLabel(enterprise)}
      </span>
      <span className="mt-0.5 flex items-start gap-1.5">
        <span
          aria-hidden="true"
          className={cn(
            "mt-[5px] h-[6px] w-[6px] shrink-0 rounded-full",
            DOT_TONES[enterpriseTone(enterprise)]
          )}
        />
        <span
          className={cn(
            "text-[11.5px] leading-[1.45] text-[var(--muted)] font-sans",
            oneLine ? "truncate" : "break-words"
          )}
        >
          {enterpriseSubtitle(enterprise)}
        </span>
      </span>
    </span>
  </>
);

export const EnterprisePicker = ({
  enterprises = [],
  selected,
  onSelect = () => {},
  label = "Tracking as",
  menuLabel = "Track buyers on behalf of",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  /* A menu left open behind a click elsewhere would sit over the page. The
     menu is portalled out of the trigger's subtree, so it needs checking
     separately or a click on a row would close it before the click landed. */
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  /* Measured before paint so the menu never appears in the wrong place first. */
  useLayoutEffect(() => {
    if (!open) return undefined;

    const measure = () => {
      const box = triggerRef.current?.getBoundingClientRect();
      if (box) setRect({ top: box.bottom + 6, left: box.left, width: box.width });
    };
    measure();

    /* A fixed menu does not travel with a scrolling container, so rather than
       drift away from its trigger it closes — unless the scroll is its own
       list's. This listener is on the capture phase, so it sees that too. */
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const close = () => setOpen(false);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  if (enterprises.length === 0) return null;

  const choose = (enterprise) => {
    setOpen(false);
    /* Guarded at the call site too, but re-choosing the same programme would
       reset the page for nothing. */
    if (enterprise && enterprise?.id !== selected?.id) onSelect(enterprise);
  };

  return (
    <div className={className}>
      <p
        id="u1-enterprise-picker-label"
        className="m-0 mb-1.5 text-[10.5px] font-bold uppercase tracking-[.08em] text-[var(--muted)] font-sans"
      >
        {label}
      </p>

      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby="u1-enterprise-picker-label"
        className={cn(
          "flex w-full items-center gap-2.5 rounded-[12px] border bg-white px-3 py-2.5 text-left",
          "transition-[border-color,box-shadow] duration-150",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]",
          /* Coral while open, as the design draws the active control. */
          open
            ? "border-[var(--coral)] shadow-[0_0_0_1px_var(--coral)]"
            : "border-[var(--line)] hover:border-[var(--field-border-hover)]"
        )}
      >
        <Identity enterprise={selected || enterprises[0]} oneLine />
        <span aria-hidden="true" className="shrink-0 text-[var(--muted)]">
          <ChevronDown open={open} />
        </span>
      </button>

      {open &&
        createPortal(
          /* Wrapped in u1-root: every token this menu paints with is scoped to
             that class, and the portal lands outside it. */
          <div className="u1-root">
            <div
              ref={menuRef}
              role="listbox"
              aria-label={menuLabel}
              className={cn(
                "z-[80] overflow-hidden rounded-[14px] border border-[var(--line)]",
                "bg-white shadow-[shadow:var(--menu-shadow)]"
              )}
              style={
                rect
                  ? {
                      position: "fixed",
                      top: rect.top,
                      left: rect.left,
                      /* The trigger's width, which on a phone is the column. */
                      width: Math.max(rect.width, 260),
                    }
                  : { position: "fixed", visibility: "hidden" }
              }
            >
              <p className="m-0 border-b border-[var(--line)] px-3.5 py-2.5 text-[10.5px] font-bold uppercase tracking-[.08em] text-[var(--muted)] font-sans">
                {menuLabel}
              </p>

              <div className="max-h-[280px] overflow-y-auto p-1.5">
                {enterprises.map((enterprise) => {
                  const isCurrent = enterprise?.id === selected?.id;
                  return (
                    <button
                      key={enterprise?.id}
                      type="button"
                      role="option"
                      aria-selected={isCurrent}
                      onClick={() => choose(enterprise)}
                      className={cn(
                        "flex w-full items-start gap-2.5 rounded-[10px] px-2 py-2 text-left",
                        "transition-colors focus-visible:outline-none focus-visible:ring-2",
                        "focus-visible:ring-inset focus-visible:ring-[var(--coral)]",
                        isCurrent
                          ? "bg-[var(--coral-soft)]"
                          : "hover:bg-[var(--bg)]"
                      )}
                    >
                      <Identity enterprise={enterprise} />
                      {isCurrent && (
                        <span
                          aria-hidden="true"
                          className="mt-0.5 shrink-0 text-[var(--coral)]"
                        >
                          <CheckIcon size={14} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default EnterprisePicker;
